import React from "react";
import "./FlyToCart.css";

/**
 * The "small thing going into the cart".
 *
 * A clone of the product photo that arcs from the product image up into the
 * cart icon in the navbar, shrinking as it goes, and disappears the instant it
 * lands — at which point the cart icon itself reacts.
 *
 * RELIABILITY
 * -----------
 * Three earlier animations on this site never played in production because
 * they waited on requestAnimationFrame or a setTimeout that was throttled to
 * never firing. So this component depends on neither.
 *
 *   - The wrapper is ALWAYS mounted. Nothing appears "on the next frame".
 *   - The flight is one CSS keyframe animation, replayed by React remounting
 *     the node (its `key` is a counter). A fresh element restarts its
 *     animation — that is a guarantee of the DOM, not of a timer.
 *   - It ends on `opacity: 0` with `animation-fill-mode: forwards`, so it
 *     cleans itself up visually without any JS teardown. It never needs to be
 *     unmounted, so nothing can leave it stuck on screen.
 *   - The measurements (source rect, target rect, distance, end scale) are
 *     taken synchronously in the click handler and handed in as inline custom
 *     properties. No layout read is deferred.
 *
 * It is purely decorative: if `flight` is null — no image, image scrolled out
 * of view, cart icon not measurable — nothing renders and the cart icon still
 * reacts on its own. The confirmation matters; the confetti does not.
 *
 * Props:
 *   flight — null, or { key, image, left, top, width, height, dx, dy, scale }
 *            in viewport (fixed-position) pixels.
 */
/** How wide the clone should be when it lands, in px. Roughly the cart glyph. */
const LANDING_SIZE = 22;

/**
 * Turn a source rectangle and the cart element into a flight description.
 * Pure and synchronous — call it inside the event handler, never later.
 *
 * Returns null when there is nothing sensible to animate, which is a normal
 * outcome, not an error: the caller falls back to reacting immediately.
 */
export function computeFlight(rect, targetEl, image, key) {
  if (!rect || !targetEl || !image) return null;
  if (!rect.width || !rect.height) return null;

  // Off-screen source: the visitor is not looking at the photo, so a clone
  // sliding in from nowhere would just be confusing.
  const vw = window.innerWidth || document.documentElement.clientWidth;
  const vh = window.innerHeight || document.documentElement.clientHeight;
  if (rect.bottom < 0 || rect.top > vh || rect.right < 0 || rect.left > vw) return null;

  const target = targetEl.getBoundingClientRect();
  if (!target.width) return null;

  // Cap the clone so a full-bleed product photo does not black out the page on
  // the way up. Keep the aspect ratio of the source.
  const maxSide = Math.min(180, vw * 0.34);
  const ratio = Math.min(1, maxSide / Math.max(rect.width, rect.height));
  const width = rect.width * ratio;
  const height = rect.height * ratio;
  const left = rect.left + (rect.width - width) / 2;
  const top = rect.top + (rect.height - height) / 2;

  return {
    key,
    image,
    left,
    top,
    width,
    height,
    dx: target.left + target.width / 2 - (left + width / 2),
    dy: target.top + target.height / 2 - (top + height / 2),
    scale: Math.max(0.04, Math.min(0.45, LANDING_SIZE / width)),
  };
}

const FlyToCart = ({ flight }) => {
  if (!flight) return null;

  return (
    <img
      key={flight.key}
      className="fly-to-cart"
      src={flight.image}
      alt=""
      aria-hidden="true"
      draggable="false"
      style={{
        left: `${flight.left}px`,
        top: `${flight.top}px`,
        width: `${flight.width}px`,
        height: `${flight.height}px`,
        "--fx": `${flight.dx}px`,
        "--fy": `${flight.dy}px`,
        "--fs": flight.scale,
      }}
    />
  );
};

export default FlyToCart;
